import {
  MAX_SERIES_DAYS,
  MAX_SKU_PICKS,
  ORDER_SERIES_BUCKETS,
  bucketStart,
  daysBetween,
  isSeriesDate,
  shiftDate,
  type IsoDate,
  type OrderSeriesBucket,
  type SkuSeriesItem,
} from "@estelle/domain";

/**
 * What Reports > Products is set to, and how that lives in the URL (#678).
 *
 * One `chart` parameter, as Orders' chart and the Sales report keep theirs: the
 * Products section declares no fields, so the toolbar writes nothing else, and
 * `SectionPage` carries `chart` through every other change. The picked SKUs'
 * keys are percent-encoded inside it so a comma, a colon or a bar in a product
 * name cannot be read as the next setting.
 */
export type ProductsView = "best-sellers" | "trends";

export interface ProductsState {
  view: ProductsView;
  bucket: OrderSeriesBucket;
  /** Both or neither. Absent means the last three months, ending today. */
  range: { from: IsoDate; to: IsoDate } | null;
  includeCancelled: boolean;
  /** The SKUs to draw, by key. Empty draws the best sellers in the range. */
  picks: string[];
}

export const DEFAULT_PRODUCTS: ProductsState = {
  view: "best-sellers",
  // Three months by day is a thicket; by week reads as a trend.
  bucket: "week",
  range: null,
  includeCancelled: false,
  picks: [],
};

export const PRODUCT_VIEWS: readonly { value: ProductsView; label: string }[] =
  [
    { value: "best-sellers", label: "Best sellers" },
    { value: "trends", label: "SKU trends" },
  ];

/** The quick ranges this view offers; a custom range is the date picker. */
export type TrendPreset = "last-3-months" | "last-month" | "month-to-date";

export const TREND_PRESETS: readonly { id: TrendPreset; label: string }[] = [
  { id: "last-3-months", label: "Last 3 months" },
  { id: "last-month", label: "Last month" },
  { id: "month-to-date", label: "Month to date" },
];

/**
 * `date` moved back by whole calendar months, clamped to the end of a shorter
 * month: 31 May less three months is 29 or 28 February, not 3 March.
 */
function monthsBefore(date: IsoDate, months: number): IsoDate {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7)) - 1 - months;
  const target = new Date(Date.UTC(year, month, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const day = Math.min(Number(date.slice(8, 10)), lastDay);
  const pad = (value: number, width: number) =>
    String(value).padStart(width, "0");
  return `${pad(target.getUTCFullYear(), 4)}-${pad(target.getUTCMonth() + 1, 2)}-${pad(day, 2)}`;
}

/**
 * A preset as dates, in the store's calendar. "Last 3 months" is the three
 * months up to and including today (the day three months ago is the first day
 * after the range's start, so it is three months and not three and a day);
 * "Last month" is the whole of the previous calendar month; "Month to date"
 * is the 1st to today, not to the end of a month that has not happened.
 */
export function trendPresetRange(
  preset: TrendPreset,
  today: IsoDate,
): { from: IsoDate; to: IsoDate } {
  switch (preset) {
    case "last-3-months":
      return { from: shiftDate(monthsBefore(today, 3), 1), to: today };
    case "month-to-date":
      return { from: bucketStart(today, "month"), to: today };
    case "last-month": {
      const lastOfPrevious = shiftDate(bucketStart(today, "month"), -1);
      return { from: bucketStart(lastOfPrevious, "month"), to: lastOfPrevious };
    }
  }
}

function oneOf<T extends string>(
  value: string | undefined,
  allowed: readonly T[],
): T | undefined {
  return allowed.find((candidate) => candidate === value);
}

function decode(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return "";
  }
}

/**
 * Read the `chart` parameter. Tolerant, like the other readers: a link someone
 * truncated or edited by hand opens the default view, and each part that does
 * not parse falls back on its own.
 */
export function decodeProducts(raw: string | undefined): ProductsState {
  const state: ProductsState = { ...DEFAULT_PRODUCTS, picks: [] };
  if (!raw) return state;

  const parts = new Map<string, string>();
  for (const pair of raw.split(",")) {
    const colon = pair.indexOf(":");
    if (colon > 0) parts.set(pair.slice(0, colon), pair.slice(colon + 1));
  }

  state.view =
    oneOf(
      parts.get("view"),
      PRODUCT_VIEWS.map((view) => view.value),
    ) ?? "best-sellers";
  state.bucket =
    oneOf(parts.get("bucket"), ORDER_SERIES_BUCKETS) ?? DEFAULT_PRODUCTS.bucket;
  state.includeCancelled = parts.get("cancelled") === "1";
  state.picks = [
    ...new Set(
      (parts.get("pick") ?? "")
        .split(";")
        .map(decode)
        .filter((key) => key !== ""),
    ),
  ].slice(0, MAX_SKU_PICKS);

  const from = parts.get("from");
  const to = parts.get("to");
  if (
    isSeriesDate(from) &&
    isSeriesDate(to) &&
    from <= to &&
    daysBetween(from, to) + 1 <= MAX_SERIES_DAYS
  ) {
    state.range = { from, to };
  }
  return state;
}

/** The `chart` parameter for a state, or `undefined` at the defaults. */
export function encodeProducts(state: ProductsState): string | undefined {
  const parts: string[] = [];
  if (state.view !== DEFAULT_PRODUCTS.view) parts.push(`view:${state.view}`);
  if (state.bucket !== DEFAULT_PRODUCTS.bucket) {
    parts.push(`bucket:${state.bucket}`);
  }
  if (state.range) {
    parts.push(`from:${state.range.from}`, `to:${state.range.to}`);
  }
  if (state.includeCancelled) parts.push("cancelled:1");
  if (state.picks.length > 0) {
    parts.push(`pick:${state.picks.map(encodeURIComponent).join(";")}`);
  }
  return parts.length === 0 ? undefined : parts.join(",");
}

/** The dates the view draws: the link's, else the last three months. */
export function productsRange(
  state: ProductsState,
  today: IsoDate,
): { from: IsoDate; to: IsoDate } {
  return state.range ?? trendPresetRange("last-3-months", today);
}

/** A SKU's colour on both charts and its chip: by its place in the picks. */
export function pickColour(index: number): string {
  return `var(--series-${String((index % MAX_SKU_PICKS) + 1)})`;
}

/** A SKU's name in a legend, a chip or a table: its code, else its name. */
export function skuLabel(
  item: Pick<SkuSeriesItem, "sku" | "productTitle" | "variantTitle">,
): string {
  if (item.sku) return item.sku;
  return item.variantTitle
    ? `${item.productTitle} · ${item.variantTitle}`
    : item.productTitle;
}

/** The product's own name, with its variant, for a SKU's second line. */
export function skuProduct(
  item: Pick<SkuSeriesItem, "productTitle" | "variantTitle">,
): string {
  return item.variantTitle
    ? `${item.productTitle} · ${item.variantTitle}`
    : item.productTitle;
}

/** Whether a SKU matches what was typed in the picker: code, name or variant. */
export function matchesSearch(
  item: Pick<SkuSeriesItem, "sku" | "productTitle" | "variantTitle">,
  text: string,
): boolean {
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return [item.sku ?? "", item.productTitle, item.variantTitle].some((part) =>
    part.toLowerCase().includes(needle),
  );
}

/** What a table of every SKU can be ordered by. */
export type SkuTableOrder = "units" | "amount" | "sku";

export const SKU_TABLE_ORDERS: readonly {
  value: SkuTableOrder;
  label: string;
}[] = [
  { value: "units", label: "Units" },
  { value: "amount", label: "Sales" },
  { value: "sku", label: "SKU" },
];

/**
 * The SKU table in the order asked for. Ties fall back to units and then to
 * the SKU, so the order never depends on how the API happened to list them.
 */
export function orderSkuItems(
  items: readonly SkuSeriesItem[],
  by: SkuTableOrder,
): SkuSeriesItem[] {
  const text = (a: string, b: string) => a.localeCompare(b);
  return [...items].sort((a, b) => {
    if (by === "sku") return text(skuLabel(a), skuLabel(b));
    if (by === "amount") {
      const difference = Number(b.amount) - Number(a.amount);
      if (difference !== 0) return difference;
    }
    if (b.units !== a.units) return b.units - a.units;
    return text(skuLabel(a), skuLabel(b));
  });
}

/** `part` as a percentage of `total`, 0 when there is no total. */
export function sharePercent(part: number, total: number): number {
  return total > 0 ? (part / total) * 100 : 0;
}

/** A share as it is written: one decimal under 10%, whole above, "—" of nothing. */
export function shareText(part: number, total: number): string {
  if (total <= 0) return "—";
  const percent = sharePercent(part, total);
  if (percent > 0 && percent < 0.1) return "<0.1%";
  return `${percent.toFixed(percent < 10 ? 1 : 0)}%`;
}
